import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["overlay", "search", "list", "form"]
  static values = { current: String, pageAction: String }

  connect() {
    this._onOpen = () => this.open()
    this._onKeydown = this._onKeydown.bind(this)
    window.addEventListener("location-picker:open", this._onOpen)
  }

  disconnect() {
    window.removeEventListener("location-picker:open", this._onOpen)
    document.removeEventListener("keydown", this._onKeydown)
    document.body.style.overflow = ""
  }

  open() {
    if (!this.hasOverlayTarget) return
    this.overlayTarget.classList.remove("hidden")
    // Sync focus before rAF — required for iOS keyboard to appear
    if (this.hasSearchTarget) this.searchTarget.focus()
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.overlayTarget.classList.remove("opacity-0")
        this.overlayTarget.classList.add("opacity-100")
        const card = this.overlayTarget.querySelector("[data-card]")
        if (card) {
          card.classList.remove("scale-95", "-translate-y-2")
          card.classList.add("scale-100", "translate-y-0")
        }
      })
    })
    document.body.style.overflow = "hidden"
    document.addEventListener("keydown", this._onKeydown)
    this._loadCities()
  }

  close() {
    if (!this.hasOverlayTarget) return
    const card = this.overlayTarget.querySelector("[data-card]")
    this.overlayTarget.classList.remove("opacity-100")
    this.overlayTarget.classList.add("opacity-0")
    if (card) {
      card.classList.remove("scale-100", "translate-y-0")
      card.classList.add("scale-95", "-translate-y-2")
    }
    setTimeout(() => this.overlayTarget.classList.add("hidden"), 200)
    document.body.style.overflow = ""
    document.removeEventListener("keydown", this._onKeydown)
    if (this.hasSearchTarget) {
      this.searchTarget.value = ""
    }
  }

  closeOnBackdrop(event) {
    if (!event.target.closest("[data-card]")) this.close()
  }

  filter() {
    if (this._data) this._refresh()
  }

  select(event) {
    event.preventDefault()
    const value = event.currentTarget.dataset.value
    if (!this.hasFormTarget || !value) return
    const input = this.formTarget.querySelector('input[name="location"]')
    if (input) input.value = value
    this._addHidden("location_source", "picker")
    this._addHidden("location_change_by_js", "true")
    this._addHidden("redirect_from", window.location.href)
    this._addHidden("controller_action", this.pageActionValue || "")
    this.formTarget.submit()
  }

  async _loadCities() {
    if (this._loaded) return
    this._loaded = true
    if (this.hasListTarget) {
      this.listTarget.innerHTML = '<div class="px-4 py-8 text-center text-sm text-muted">Loading…</div>'
    }
    try {
      // Send the current locale so city/country titles come back translated, and
      // keep it in the URL so the browser caches per-locale (not one stale copy).
      const locale = document.documentElement.lang || "en"
      const resp = await fetch(`/api/v1/cities?locale=${encodeURIComponent(locale)}`, {
        headers: { Accept: "application/json" },
        cache: "no-store"
      })
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`)
      this._data = await resp.json()
      this._refresh()
    } catch (e) {
      console.error("location-picker: failed to load cities", e)
      if (this.hasListTarget) {
        this.listTarget.innerHTML = '<div class="px-4 py-8 text-center text-sm text-muted">Unable to load locations.</div>'
      }
      this._loaded = false
    }
  }

  _refresh() {
    if (!this._data || !this.hasListTarget) return
    const query = this.hasSearchTarget ? this.searchTarget.value.toLowerCase().trim() : ""
    if (query) {
      this._renderFiltered(query)
    } else {
      this._renderDefault()
    }
  }

  _renderDefault() {
    const data = this._data
    const current = this.currentValue || ""
    const parts = current.split("_")
    const type = parts[0]
    const id = parseInt(parts[1], 10)

    let suggested = []

    if (type === "city" && id) {
      const cur = data.cities.find(c => c.id === id)
      if (cur) {
        // State cities first
        if (cur.state_id) {
          suggested = data.cities.filter(c => c.state_id === cur.state_id)
          // Put current city first
          suggested.sort((a, b) => (a.id === id ? -1 : b.id === id ? 1 : 0))
          suggested = suggested.slice(0, 5)
        }
        // Fill from country
        if (suggested.length < 5 && cur.country_id) {
          const seen = new Set(suggested.map(s => s.id))
          const more = data.cities.filter(c => c.country_id === cur.country_id && !seen.has(c.id))
          suggested = suggested.concat(more.slice(0, 5 - suggested.length))
        }
      }
    } else if (type === "country" && id) {
      suggested = data.cities.filter(c => c.country_id === id).slice(0, 5)
    }

    // Fill remainder with India
    if (suggested.length < 5) {
      const india = data.countries.find(c => c.short_code === "IN" || c.slug === "india")
      if (india) {
        const seen = new Set(suggested.map(s => s.id))
        const more = data.cities.filter(c => c.country_id === india.id && !seen.has(c.id))
        suggested = suggested.concat(more.slice(0, 5 - suggested.length))
      }
    }

    this._renderCityButtons(suggested)
  }

  _renderFiltered(query) {
    const data = this._data
    const current = this.currentValue
    const countryMap = {}
    ;(data.countries || []).forEach(c => { countryMap[c.id] = c })

    const groups = {}
    const countryOrder = []

    ;(data.countries || []).forEach(country => {
      const nameMatch = country.title.toLowerCase().includes(query)
      if (nameMatch) {
        if (!groups[country.id]) {
          groups[country.id] = { country, cities: [], countryMatch: true }
          countryOrder.push(country.id)
        }
      }
    })

    ;(data.cities || []).forEach(city => {
      const match = city.title.toLowerCase().includes(query) ||
                    (city.country_title || "").toLowerCase().includes(query)
      if (!match) return
      if (!groups[city.country_id]) {
        groups[city.country_id] = { country: countryMap[city.country_id] || {}, cities: [], countryMatch: false }
        countryOrder.push(city.country_id)
      }
      groups[city.country_id].cities.push(city)
    })

    let html = ""
    countryOrder.forEach(countryId => {
      const g = groups[countryId]
      const country = g.country
      const countryValue = country.id ? `country_${country.id}` : ""
      const isCurrentCountry = countryValue && current === countryValue

      html += `<div data-group>`
      html += `<div data-group-header class="sticky top-0 bg-canvas/95 backdrop-blur-sm px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider text-muted/70 z-10">${this._esc(country.title || "")}</div>`

      if (countryValue) {
        html += `<button type="button" data-item data-label="${this._esc((country.title || "").toLowerCase())}" data-value="${countryValue}" data-action="click->location-picker#select"
          class="w-full flex items-center gap-2.5 rounded-lg px-3 py-2.5 text-left transition-colors cursor-pointer ${isCurrentCountry ? "bg-primary/8" : "hover:bg-surface-soft"}">
          <svg class="h-4 w-4 ${isCurrentCountry ? "text-primary" : "text-muted"} shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><path d="M2 12h20M12 2a15.3 15.3 0 010 20M12 2a15.3 15.3 0 000 20"/></svg>
          <span class="flex-1 text-sm font-semibold ${isCurrentCountry ? "text-primary" : "text-ink dark:text-white"} truncate">${this._esc(country.title || "")}</span>
          ${isCurrentCountry ? '<svg class="ml-auto h-4 w-4 text-primary shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="3"><path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7"/></svg>' : ""}
        </button>`
      }

      g.cities.forEach(city => {
        const cityValue = `city_${city.id}`
        const isCurrentCity = current === cityValue
        html += `<button type="button" data-item data-label="${this._esc(city.title.toLowerCase())} ${this._esc((city.country_title || "").toLowerCase())}" data-value="${cityValue}" data-action="click->location-picker#select"
          class="w-full flex items-center gap-2.5 rounded-lg px-3 py-2.5 text-left transition-colors cursor-pointer ${isCurrentCity ? "bg-primary/8" : "hover:bg-surface-soft"}">
          <svg class="h-3.5 w-3.5 ${isCurrentCity ? "text-primary" : "text-muted/60"} shrink-0 ml-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"/><path stroke-linecap="round" stroke-linejoin="round" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"/></svg>
          <span class="flex-1 text-sm ${isCurrentCity ? "font-semibold text-primary" : "font-medium text-ink dark:text-white"} truncate">${this._esc(city.title)}</span>
          ${isCurrentCity ? '<svg class="ml-auto h-4 w-4 text-primary shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="3"><path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7"/></svg>' : ""}
        </button>`
      })
      html += `</div>`
    })

    this.listTarget.innerHTML = html || `<div class="px-4 py-8 text-center text-sm text-muted">No results for "${this._esc(query)}"</div>`
  }

  _renderCityButtons(cities) {
    if (!this.hasListTarget) return
    const current = this.currentValue
    let html = ""
    cities.forEach(city => {
      const cityValue = `city_${city.id}`
      const isCurrentCity = current === cityValue
      html += `<button type="button" data-value="${cityValue}" data-action="click->location-picker#select"
        class="w-full flex items-center gap-2.5 rounded-lg px-3 py-2.5 text-left transition-colors cursor-pointer ${isCurrentCity ? "bg-primary/8" : "hover:bg-surface-soft"}">
        <svg class="h-3.5 w-3.5 ${isCurrentCity ? "text-primary" : "text-muted/60"} shrink-0 ml-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"/><path stroke-linecap="round" stroke-linejoin="round" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"/></svg>
        <span class="flex-1 min-w-0">
          <span class="block text-sm ${isCurrentCity ? "font-semibold text-primary" : "font-medium text-ink dark:text-white"} truncate">${this._esc(city.title)}</span>
          <span class="block text-xs text-muted truncate">${this._esc(city.country_title || "")}</span>
        </span>
        ${isCurrentCity ? '<svg class="ml-auto h-4 w-4 text-primary shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="3"><path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7"/></svg>' : ""}
      </button>`
    })
    this.listTarget.innerHTML = html || '<div class="px-4 py-8 text-center text-sm text-muted">No nearby cities found.</div>'
  }

  _addHidden(name, value) {
    if (!this.hasFormTarget) return
    let el = this.formTarget.querySelector(`input[name="${name}"]`)
    if (!el) {
      el = document.createElement("input")
      el.type = "hidden"; el.name = name
      this.formTarget.appendChild(el)
    }
    el.value = value
  }

  _esc(str) {
    return (str || "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;")
  }

  _onKeydown(e) {
    if (e.key === "Escape") this.close()
  }
}
